import { message } from 'antd';
import type { AxiosResponse } from 'axios';
import chunk from 'lodash/chunk';
import keyBy from 'lodash/keyBy';
import Router from 'next/router';
import { all, call, delay, put, select, takeLatest } from 'redux-saga/effects';
import type { ActionType } from 'typesafe-actions';

import { UserDeactivationReason } from '@evenfinancial/auth-client';
import type {
  RichAccessToken,
  UserDeactivation,
} from '@evenfinancial/auth-client';

import { getRichAccessTokens } from '@portal/api-client/src/auth';
import { client } from '@portal/api-client/src/client';
import { createUser, updateUser } from '@portal/api-client/src/user';
import type {
  Roles,
  UserCreateData,
  UserGrantsData,
  UserUpdateData,
  UserWithRoles,
} from '@portal/api-models';
import { getPortalError, toUserUpdateData } from '@portal/common';

import type { ApplicationState } from '../types';

import { UserActionType } from './actionTypes';
import * as actions from './actions';
import {
  selectPermissions,
  selectUserRoles,
  selectUsersByUuid,
} from './selectors';

export function* getUsers() {
  try {
    const { data }: AxiosResponse<UserWithRoles[]> = yield call(
      client.get,
      '/user'
    );

    yield put(actions.userRequestAction.success(data));
  } catch (err: any) {
    yield put(actions.userRequestAction.failure(err));
  }
}

export function* getUsersAccess() {
  try {
    const { data }: AxiosResponse<UserGrantsData[]> = yield call(
      client.get,
      '/user/access'
    );

    yield put(actions.userAccessRequestAction.success(data));
  } catch (err: any) {
    yield put(actions.userAccessRequestAction.failure(err));
  }
}

export function* getUserByUuid(
  action: ActionType<typeof actions.userByUuidRequestAction.request>
) {
  try {
    const {
      user: { accessTokenById },
    } = yield select((state: ApplicationState) => state);

    const { uuid } = action.payload;
    const { data: user }: AxiosResponse<UserWithRoles> = yield call(
      client.get,
      `/user/${uuid}`
    );

    if (
      user.createdById &&
      !Object.prototype.hasOwnProperty.call(accessTokenById, user.createdById)
    ) {
      yield put(
        actions.richAccessTokenByIdRequestAction.request({
          id: user.createdById,
        })
      );
    }

    yield put(actions.userByUuidRequestAction.success(user));
  } catch (err: any) {
    yield call([Router, 'replace'], '/404');
    yield put(actions.userByUuidRequestAction.failure(err));
  }
}

export function* getUserAccessByUuidRequest(
  action: ActionType<typeof actions.userAccessByUuidRequestAction.request>
) {
  try {
    const { uuid } = action.payload;

    const { data }: AxiosResponse<UserGrantsData> = yield call(
      client.get,
      `/user/${uuid}/access`
    );

    yield put(actions.userAccessByUuidRequestAction.success(data));
  } catch (err: any) {
    yield put(actions.userAccessByUuidRequestAction.failure(err));
  }
}

export function* createUserSaga(
  action: ActionType<typeof actions.userCreateRequestAction.request>
) {
  try {
    const {
      accounts,
      allAccounts,
      subAccounts,
      roleObject,
      supplySubAccounts,
      demandSubAccounts,
      partnerType,
      demandRole,
      supplyRole,
      ...formParams
    } = action.payload;

    const roles: Roles[] = yield select(selectUserRoles);
    const isInternal = partnerType === undefined;
    const getUuids = <T extends { uuid: string }>(
      items?: (string | number | T)[]
    ) => {
      if ((allAccounts as any) === 'true' || Boolean(allAccounts) === true) {
        return [];
      }

      return items?.map((item) => {
        if (typeof item === 'object') {
          return item.uuid;
        }

        return item;
      });
    };

    // Combine the supplySubAccounts array and demandSubAccounts array.
    const newSubAccounts: any[] = [];

    if (supplySubAccounts?.length) {
      newSubAccounts.push(...supplySubAccounts);
    }

    if (demandSubAccounts?.length) {
      newSubAccounts.push(...demandSubAccounts);
    }

    // Combine the demand and supply roles into one role object.
    const newRoles: Roles[] = [];

    if (demandRole) {
      newRoles.push(...demandRole.roles);
    }

    if (supplyRole) {
      newRoles.push(...supplyRole.roles);
    }

    // roleObject only has value if they are internal
    if (roleObject) {
      newRoles.push(...roleObject.roles);
    }

    const { shouldForceCurrentUserRolesOnUserCreation } = yield select(
      selectPermissions
    );

    const data: UserCreateData = {
      ...formParams,
      accountUuids: getUuids(accounts) as string[],
      emailAddress: formParams.emailAddress?.trim(),
      roles: shouldForceCurrentUserRolesOnUserCreation ? roles : newRoles,
      subAccountUuids: isInternal
        ? (getUuids(subAccounts) as string[])
        : (getUuids(newSubAccounts) as string[]),
    };

    const createdUser: UserWithRoles = yield call(createUser, data);

    yield put(actions.userCreateRequestAction.success(createdUser));
    yield put(actions.userRequestAction.request({}));
    yield put(actions.userAccessRequestAction.request({}));

    yield put(actions.toggleCreateUser());
    yield delay(100);
    yield call([message, 'success'], 'The user was created successfully');
  } catch (err: any) {
    const portalError = getPortalError(err);

    yield put(actions.userCreateRequestAction.failure(portalError));
  }
}

export function* toggleDuplicate(
  action: ActionType<typeof actions.toggleDuplicateUser>
) {
  try {
    const uuid = action.payload;

    yield all([
      put(actions.userByUuidRequestAction.request({ uuid })),
      put(actions.userAccessByUuidRequestAction.request({ uuid })),
    ]);

    yield put(actions.toggleDuplicateOpen(uuid));
  } catch (err) {
    return err;
  }
}

// NOSONAR - Ignoring cognitive complexity for this function
export function* updateUserRequest(
  action: ActionType<typeof actions.updateUserRequestAction.request>
) {
  try {
    const { access, user, form } = action.payload;

    const updateData = toUserUpdateData(form);

    const accountUuids =
      updateData?.accountUuids ?? access.grants.accounts ?? [];

    const subAccountUuids =
      updateData?.subAccountUuids ?? access.grants.subaccounts ?? [];

    const emailAddress = updateData?.emailAddress ?? user.emailAddress;
    const firstName = updateData?.firstName ?? user.firstName;
    const lastName = updateData?.lastName ?? user.lastName;
    const roles = updateData?.roles ?? (access.roles as Roles[]);

    const mergedUpdateData: UserUpdateData = {
      accountUuids,
      emailAddress,
      firstName,
      lastName,
      roles,
      subAccountUuids,
    };

    const updatedUser: UserWithRoles = yield call(
      updateUser,
      user.uuid,
      mergedUpdateData
    );

    yield put(actions.updateUserRequestAction.success(updatedUser)); // not handled anywhere b/c doing another get below

    yield put(
      actions.userByUuidRequestAction.request({ uuid: updatedUser.uuid })
    );

    yield put(
      actions.userAccessByUuidRequestAction.request({ uuid: updatedUser.uuid })
    );

    setTimeout(() => message.success('The user was updated successfully'), 100);
  } catch (err: any) {
    yield put(actions.updateUserRequestAction.failure(err));
    yield call([message, 'error'], 'There was an error updating the user');
  }
}
export function* getRichAccessTokenByUuid(
  action: ActionType<typeof actions.richAccessTokenByUuidRequestAction.request>
) {
  try {
    const chunkedList: [string[]] = yield call(chunk, action.payload.uuids, 40);

    const richAccessTokens: RichAccessToken[] = yield all(
      chunkedList.map((chunk) =>
        call(getRichAccessTokens, { accessTokenUuids: chunk })
      )
    );

    yield put(
      actions.richAccessTokenByUuidRequestAction.success(
        keyBy(
          richAccessTokens.flatMap((t) => t),
          'uuid'
        )
      )
    );
  } catch (err: any) {
    yield put(actions.richAccessTokenByUuidRequestAction.failure(err));
  }
}

export function* getRichAccessTokenById(
  action: ActionType<typeof actions.richAccessTokenByIdRequestAction.request>
) {
  try {
    const { id } = action.payload;

    const richAccessTokens: RichAccessToken[] = yield call(
      getRichAccessTokens,
      { id }
    );

    if (richAccessTokens.length) {
      yield put(
        actions.richAccessTokenByIdRequestAction.success({
          id,
          richAccessToken: richAccessTokens[0],
        })
      );
    }
  } catch (err: any) {
    yield put(actions.richAccessTokenByIdRequestAction.failure(err));
  }
}

export function* deleteAccessToken(
  action: ActionType<typeof actions.deleteAccessTokenByUuid.request>
) {
  try {
    yield call(client.delete, `/auth/access_tokens/${action.payload.uuid}`);

    yield put(
      actions.deleteAccessTokenByUuid.success({
        subAccountUuid: action.payload.subAccountUuid,
        uuid: action.payload.uuid,
      })
    );

    setTimeout(
      () => message.success('The access token was deleted successfully'),
      100
    );
  } catch (err: any) {
    yield put(actions.deleteAccessTokenByUuid.failure(err));
  }
}

interface GetUserByUuidRequest {
  cookie?: string;
  uuid: string | undefined;
}

export function* getUserByUuidRequest({ uuid }: GetUserByUuidRequest) {
  try {
    if (uuid) {
      const usersByUuid: Record<string, number> = yield select(
        selectUsersByUuid
      );

      if (!Object.prototype.hasOwnProperty.call(usersByUuid, uuid!)) {
        yield put(actions.userByUuidRequestAction.request({ uuid }));
      }
    }
  } catch (err) {
    return err;
  }
}

export function* userPasswordReset(
  action: ActionType<typeof actions.userPwResetAction.request>
) {
  try {
    yield call(client.post, `/user/${action.payload}/reset_password`);
    yield put(actions.userPwResetAction.success());
    setTimeout(
      () =>
        message.success('A password reset email has been sent to the user.'),
      100
    );
  } catch (err: any) {
    yield put(actions.userPwResetAction.failure(err));
  }
}

export function* userVerification(
  action: ActionType<typeof actions.userVerificationAction.request>
) {
  try {
    yield call(client.post, `/user/${action.payload}/verification_email`);
    yield put(actions.userVerificationAction.success());
    setTimeout(
      () => message.success('A verification email has been sent to the user.'),
      100
    );
  } catch (err: any) {
    const portalError = getPortalError(err);

    yield put(actions.userVerificationAction.failure(portalError));
    yield call([message, 'success'], portalError.customError);
  }
}

export function* getDeactivationUsers() {
  try {
    const { data: usersDeactivation }: AxiosResponse<UserDeactivation[]> =
      yield call(client.get, '/user/user_deactivations');

    yield put(actions.userDeactivationRequestAction.success(usersDeactivation));
  } catch (err: any) {
    yield put(actions.userDeactivationRequestAction.failure(err));
  }
}

export function* createUserDeactivations(
  action: ActionType<typeof actions.createUserDeactivationsAction.request>
) {
  try {
    const { userIds, deactivationReason = UserDeactivationReason.UserAction } =
      action.payload;
    const { data: createDeactivations }: AxiosResponse<UserDeactivation[]> =
      yield call(client.post, '/user/user_deactivations', {
        deactivationReason,
        userIds,
      });

    yield put(
      actions.createUserDeactivationsAction.success(createDeactivations)
    );
    setTimeout(
      () => message.success('The User Deactivations Successfully Updated'),
      100
    );
  } catch (err: any) {
    yield put(actions.createUserDeactivationsAction.failure(err));
  }
}

export function* deleteUserDeactivation(
  action: ActionType<typeof actions.deleteUserDeactivationAction.request>
) {
  try {
    const { deactivationIds } = action.payload;
    const { data: deleteDeactivations }: AxiosResponse<UserDeactivation[]> =
      yield call(client.put, '/user/user_deactivations', { deactivationIds });

    yield put(
      actions.deleteUserDeactivationAction.success(deleteDeactivations)
    );
    setTimeout(
      () => message.success('The User Deactivations Successfully Updated'),
      100
    );
  } catch (err: any) {
    yield put(actions.deleteUserDeactivationAction.failure(err));
  }
}

export function* sendDemandContactUsRequestEmail(
  action: ActionType<
    typeof actions.sendUserDemandContactUsRequestEmailAction.request
  >
) {
  try {
    const { accountName, type } = action.payload;
    yield call(client.post, 'email/demand/contact-us-request', {
      accountName,
      type,
    });

    yield put(actions.sendUserDemandContactUsRequestEmailAction.success(type));
    yield call(message.success, 'Your request is submitted successfully!');
  } catch (err: any) {
    yield put(actions.sendUserDemandContactUsRequestEmailAction.failure(err));
    yield call(message.error, 'An error occurred!');
  }
}

export function* sendUserCreationLimitChangeRequestEmail() {
  try {
    yield call(client.post, 'email/scale/user-creation-limit-change-request');

    yield put(actions.sendUserCreationLimitChangeRequestEmailAction.success());
    yield call(message.success, 'Your request is submitted successfully!');
  } catch (err: any) {
    yield put(
      actions.sendUserCreationLimitChangeRequestEmailAction.failure(err)
    );
    yield call(message.error, 'An error occurred!');
  }
}

export function* usersSaga() {
  yield takeLatest(
    UserActionType.GET_ALL_USER_DEACTIVATION_REQUEST,
    getDeactivationUsers
  );
  yield takeLatest(
    UserActionType.CREATE_USER_DEACTIVATIONS_REQUEST,
    createUserDeactivations
  );
  yield takeLatest(
    UserActionType.DELETE_USER_DEACTIVATIONS_REQUEST,
    deleteUserDeactivation
  );
  yield takeLatest(
    UserActionType.GET_RICH_TOKEN_BY_UUID_REQUEST,
    getRichAccessTokenByUuid
  );
  yield takeLatest(
    UserActionType.GET_RICH_TOKEN_BY_ID_REQUEST,
    getRichAccessTokenById
  );
  yield takeLatest(
    UserActionType.DELETE_TOKEN_BY_UUID_REQUEST,
    deleteAccessToken
  );
  yield takeLatest(
    UserActionType.GET_ACCESS_BY_UUID_REQUEST,
    getUserAccessByUuidRequest
  );
  yield takeLatest(UserActionType.GET_ALL_REQUEST, getUsers);
  yield takeLatest(UserActionType.GET_ACCESS_ALL_REQUEST, getUsersAccess);
  yield takeLatest(UserActionType.CREATE_REQUEST_CLIENT, createUserSaga);
  yield takeLatest(UserActionType.TOGGLE_DUPLICATE, toggleDuplicate);
  yield takeLatest(UserActionType.GET_BY_UUID_REQUEST, getUserByUuid);
  yield takeLatest(UserActionType.UPDATE_REQUEST, updateUserRequest);
  yield takeLatest(UserActionType.PW_RESET_REQUEST, userPasswordReset);
  yield takeLatest(UserActionType.GET_VERIFICATION_REQUEST, userVerification);
  yield takeLatest(
    UserActionType.SEND_DEMAND_USER_CONTACT_US_REQUEST_EMAIL_REQUEST,
    sendDemandContactUsRequestEmail
  );
  yield takeLatest(
    UserActionType.SEND_USER_CREATION_LIMIT_CHANGE_REQUEST_EMAIL_REQUEST,
    sendUserCreationLimitChangeRequestEmail
  );
}
